// Co-branding клиентского SPA на subdomain дистрибьютора.
//
// До S21 это был демо-реестр из 3 захардкоженных slug-ов с переключением через
// ?distributor=<slug> query-param (D09 §2 — раняя UI-фаза без реального бэка).
// После S22/S44 backend хранит displayName/primary_color/logo на сущности
// `distributor`, и public-эндпоинт /api/public/distributor-info?slug=...
// отдаёт публичную проекцию без аутентификации. Этот провайдер:
//
//   1. резолвит slug из window.location.hostname (первый сегмент),
//   2. фетчит /api/public/distributor-info через React Query,
//   3. ставит data-distributor + CSS-vars (--color-accent*) на <html>,
//   4. отдаёт в контекст готовую запись { slug, name, short, color, hasLogo, logoUrl }.
//
// Особые хосты (admin, app, www, localhost, голый IP) — slug=null, рендерится
// платформенный layout без брендирования.
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  getPublicDistributorInfo,
  publicDistributorLogoUrl,
  type DistributorPublicInfo,
} from '@dss/api-client'
import { api } from './api'
import { brandForeground } from '@dss/design-system'

/** Хосты, которые НЕ являются distributor-slug-ом. Первый сегмент попадает
 *  в этот set → провайдер уходит в платформенный fallback (current=null). */
const RESERVED_FIRST_SEGMENTS = new Set([
  'admin',
  'app',
  'www',
  'localhost',
  '127',
  '0',
  '',
])

/** Резолвит slug из текущего hostname. Возвращает null для admin/app/www,
 *  localhost, голого IP, или хоста без поддомена (например, `ds2b.ru` без
 *  префикса). */
function resolveSlugFromHost(): string | null {
  if (typeof window === 'undefined') return null
  const host = window.location.hostname.toLowerCase().trim()
  if (host === '' || host === 'localhost') return null
  // Чистый IPv4 — пропускаем.
  if (/^\d+(\.\d+){3}$/.test(host)) return null
  const parts = host.split('.')
  // Хост без поддомена (`ds2b.ru`, `example.com`) — slug нет.
  if (parts.length < 3) return null
  const head = parts[0]
  if (!head || RESERVED_FIRST_SEGMENTS.has(head)) return null
  // Slug должен совпадать с CHECK-constraint backend-а (changeset 016
  // `ck_distributor_subdomain_slug_format`): начинается и заканчивается
  // alphanumeric, 3..32 символа, в середине допустим `-`. Если head не
  // подходит — это точно не distributor, не дёргаем endpoint лишний раз.
  if (!/^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/.test(head)) return null
  return head
}

export interface DistributorBrandRecord {
  /** Subdomain slug, например `darkside`. */
  slug: string
  /** Человекочитаемое имя бренда. Fallback на slug, если backend не отдал. */
  name: string
  /** 1-2 буквенный mark (например, «DA» / «РФ»). */
  short: string
  /** Брендовый HEX-цвет (`#RRGGBB`) или CSS-fallback, если не задан. */
  color: string
  /** Загружен ли логотип у тенанта. */
  hasLogo: boolean
  /** Готовый URL public-стрима логотипа, или null если hasLogo=false. */
  logoUrl: string | null
}

export interface DistributorBrandContextValue {
  /** null — нет подходящего slug-а (admin/localhost) или backend вернул 404. */
  current: DistributorBrandRecord | null
}

const DistributorBrandContext =
  createContext<DistributorBrandContextValue | null>(null)

/** Платформенный fallback-цвет, когда у тенанта primary_color не задан.
 *  Совпадает с цветом «D»-плейсхолдера в ClientShell до S44. */
const FALLBACK_COLOR = '#0f172a' // var(--slate-900)

function deriveShort(name: string | null | undefined, slug: string): string {
  const source = (name && name.trim().length > 0 ? name.trim() : slug).trim()
  const parts = source.split(/\s+/).filter((p) => p.length > 0)
  const [first, second] = parts
  if (first && second) {
    return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase()
  }
  return (first ?? slug).substring(0, 2).toUpperCase()
}

function toRecord(
  info: DistributorPublicInfo,
  slug: string,
): DistributorBrandRecord {
  const name = info.displayName && info.displayName.trim().length > 0
    ? info.displayName
    : slug
  const color = info.primaryColor && /^#[0-9a-fA-F]{6}$/.test(info.primaryColor)
    ? info.primaryColor.toLowerCase()
    : FALLBACK_COLOR
  return {
    slug,
    name,
    short: deriveShort(info.displayName, slug),
    color,
    hasLogo: info.hasLogo,
    logoUrl: info.hasLogo ? publicDistributorLogoUrl(api, slug) : null,
  }
}

export interface DistributorBrandProviderProps {
  children: ReactNode
}

/**
 * Резолвит slug из hostname → фетчит /api/public/distributor-info → ставит
 * data-distributor + CSS-vars на <html>.
 *
 * Жизненный цикл:
 * - mount: useQuery стартует, пока pending — current=null (платформенный layout),
 *   после success — record/null;
 * - запись меняется → useEffect ставит/снимает атрибут и CSS-переменные;
 * - unmount → cleanup-функция убирает override (на случай SSR/preview-режима).
 */
export function DistributorBrandProvider({
  children,
}: DistributorBrandProviderProps) {
  const slug = useMemo(resolveSlugFromHost, [])

  const query = useQuery({
    queryKey: ['public-distributor-info', slug],
    queryFn: () =>
      slug ? getPublicDistributorInfo(api, slug) : Promise.resolve(null),
    enabled: slug !== null,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  const current = useMemo<DistributorBrandRecord | null>(() => {
    if (!slug || !query.data) return null
    return toRecord(query.data, slug)
  }, [slug, query.data])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (current) {
      root.setAttribute('data-distributor', current.slug)
      // Накатываем accent-токены только если backend отдал валидный HEX
      // (toRecord уже нормализовал/проверил). Hover/active — тот же цвет;
      // более тонкий ladder можно сгенерировать через color-mix() уже в CSS.
      if (current.color.startsWith('#')) {
        root.style.setProperty('--color-accent', current.color)
        root.style.setProperty('--color-accent-fg', brandForeground(current.color))
        root.style.setProperty('--color-accent-border', brandForeground(current.color))
        root.style.setProperty('--color-accent-hover', current.color)
        root.style.setProperty('--color-accent-active', current.color)
      }
    } else {
      root.removeAttribute('data-distributor')
      root.style.removeProperty('--color-accent')
      root.style.removeProperty('--color-accent-fg')
      root.style.removeProperty('--color-accent-border')
      root.style.removeProperty('--color-accent-hover')
      root.style.removeProperty('--color-accent-active')
    }
    return () => {
      root.removeAttribute('data-distributor')
      root.style.removeProperty('--color-accent')
      root.style.removeProperty('--color-accent-fg')
      root.style.removeProperty('--color-accent-border')
      root.style.removeProperty('--color-accent-hover')
      root.style.removeProperty('--color-accent-active')
    }
  }, [current])

  const value = useMemo<DistributorBrandContextValue>(
    () => ({ current }),
    [current],
  )

  return (
    <DistributorBrandContext.Provider value={value}>
      {children}
    </DistributorBrandContext.Provider>
  )
}

export function useDistributorBrand(): DistributorBrandContextValue {
  const ctx = useContext(DistributorBrandContext)
  if (!ctx) {
    throw new Error(
      'useDistributorBrand must be used within <DistributorBrandProvider>',
    )
  }
  return ctx
}
